<script setup lang="ts">
import { computed } from 'vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'

interface Props {
  chartType: 'line' | 'bar'
  showValues: boolean
  selectedDateTrunc: string
  zoomScale?: number
  currentTarget?: string
}

const props = withDefaults(defineProps<Props>(), {
  zoomScale: 100,
})

const emit = defineEmits<{
  (e: 'update:chartType', val: 'line' | 'bar'): void
  (e: 'update:showValues', val: boolean): void
  (e: 'update:selectedDateTrunc', val: string): void
  (e: 'update:zoomScale', val: number): void
}>()

const localChartType = computed({
  get: () => props.chartType,
  set: val => emit('update:chartType', val),
})

const localShowValues = computed({
  get: () => props.showValues,
  set: val => emit('update:showValues', val),
})

const localDateTrunc = computed({
  get: () => props.selectedDateTrunc,
  set: val => emit('update:selectedDateTrunc', val),
})

const localZoomScale = computed({
  get: () => props.zoomScale,
  set: val => emit('update:zoomScale', val),
})
</script>

<template>
  <VMenu
    open-on-hover
    :close-on-content-click="false"
    close-delay="200"
    location="bottom end"
    offset="8"
  >
    <template #activator="{ props: menuProps }">
      <VIconBtn
        v-bind="menuProps"
        variant="plain"
        icon="ri-more-2-line"
      />
    </template>

    <VCard min-width="240" class="rounded-lg elevation-4 border bg-surface">
      <div class="text-body-1 font-weight-medium text-high-emphasis pa-4 pb-0">
        Настройки графика
      </div>
      <CustomDivider class="my-3" />
      <div class="pa-4 pt-0 d-flex flex-column gap-3">
        <div class="d-flex align-center justify-space-between gap-4">
          <span class="text-body-1 text-high-emphasis">Отображать значения</span>
          <VSwitch
            v-model="localShowValues"
            density="compact"
            hide-details
            color="primary"
          />
        </div>

        <div class="d-flex flex-column gap-1">
          <span class="text-body-1 font-weight-medium text-high-emphasis">Вид</span>
          <VRadioGroup
            v-model="localChartType"
            inline
            density="compact"
            hide-details
          >
            <VRadio
              label="Линейный"
              value="line"
              color="primary"
              class="me-4"
            />
            <VRadio label="Столбчатый" value="bar" color="primary" />
          </VRadioGroup>
        </div>

        <div v-if="!!currentTarget" class="d-flex flex-column gap-1 mt-2">
          <span class="text-body-1 font-weight-medium text-high-emphasis">Детализация</span>
          <VRadioGroup
            v-model="localDateTrunc"
            density="compact"
            hide-details
            class="mt-1"
          >
            <div class="d-flex gap-x-8 gap-y-2">
              <div class="d-flex flex-column gap-2">
                <VRadio label="Год" value="year" color="primary" />
                <VRadio label="Месяц" value="month" color="primary" />
              </div>
              <div class="d-flex flex-column gap-2">
                <VRadio label="Неделя" value="week" color="primary" />
                <VRadio label="День" value="day" color="primary" />
              </div>
            </div>
          </VRadioGroup>
        </div>

        <!-- Масштаб графика -->
        <div class="d-flex flex-column gap-1 mt-2">
          <span class="text-body-1 font-weight-medium text-high-emphasis">Масштаб</span>
          <VRadioGroup
            v-model="localZoomScale"
            density="compact"
            hide-details
            class="mt-1"
          >
            <div class="d-flex gap-x-8 gap-y-2">
              <div class="d-flex flex-column gap-2">
                <VRadio label="50%" :value="50" color="primary" />
                <VRadio label="75%" :value="75" color="primary" />
              </div>
              <div class="d-flex flex-column gap-2">
                <VRadio label="100%" :value="100" color="primary" />
                <VRadio label="125%" :value="125" color="primary" />
              </div>
            </div>
          </VRadioGroup>
        </div>
      </div>
    </VCard>
  </VMenu>
</template>
